import { Component } from 'react';
import { inject, observer } from 'mobx-react';
import moment from 'moment/min/moment-with-locales';
import { Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';

//components
import applicationRouter from '~/hoc/applicationRouter'
import withLocalization from '~/hoc/withLocalization';
import RectangularSmallCard from '../../../components/Card/RectangularSmallCard';
import TimePickerInput from '../../../components/TimePicker';
import { MembersWidget } from '../../../components/GenericForm/Widgets/UsersWidget';
import AlertMessage, { createAlert } from '../../../components/Alert/AlertMessage.jsx';
import { ProjectsWidget } from '../../../components/GenericForm/Widgets/ProjectsWidget.js';

//elements
import LoadingSpinner from '~/elements/LoadingSpinner';
import Button from '../../../components/CustomButton/CustomButton.jsx';
import Input from '../../../elements/Input.js';

//utils
import { calculateBusinessDays, calculateHoursFromTimes } from '../../../utils/commonMethods.js';
import { getMinutesFromTimeInput, minutesToTimeInput } from '../../../utils/timeCalc';
import { totalTimeFormat } from '../../../utils/dateFormat.js';

class CompOffForm extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loadedUser: null,
            user_id: null,
            total_hours: 0,
            total_days: 1,
            start_time: null,
            end_time: null,
            from: moment().format('YYYY-MM-DD'),
            to: moment().format('YYYY-MM-DD'),
            project_id: null,
            dailyContractedHours: 0,
            negativeFlexitimeHoursLimit: 0,
            userBreakMessage: null,
            userWorkingShiftFollowSchedule: false,
            autoSplitMessage: null,
            alertMessages: []
        };
        this.defaultHolidaysForVacation = props.commonStore.config.client.data.Vacation_setup?.default_holidays_for_vacation || '';
        this.enableDefaultHolidaysForAbsence = !!props.commonStore.config.client.data.Vacation_setup?.enable_default_holidays_for_absence;
        this.onSave = this.onSave.bind(this)
    }

    async handleChangeStatus() {
        this.loadData();
    }

    getBusinessDays(from, to) {
        return calculateBusinessDays(from, to, this.defaultHolidaysForVacation, this.enableDefaultHolidaysForAbsence, this.props.commonStore, 'absence');
    }

    async fetchAndSetUserData(user_id, for_date = moment().format('YYYY-MM-DD'), to_date = moment().format('YYYY-MM-DD')) {
        try {
            const { userStore, absenceStore } = this.props;
            const user = await userStore.pullUserById(user_id);
            const params = { user_id, from: for_date, to: to_date, editAbsenceId: null };
            const result = await absenceStore.getAbsenceTotals(params);
            user.contractedHours = result.employeeDailyContractedTimeInMins;
            const _states = {
                loadedUser: user,
                dailyContractedHours: result.employeeDailyContractedTimeInMins,
                negativeFlexitimeHoursLimit: result.negativeFlexitimeHoursLimit,
                start_time: result.startTime,
                end_time: result.endTime,
                total_hours: calculateHoursFromTimes(0, result.employeeDailyContractedTimeInMins),
                alertMessages: [],
                userBreakMessage: result?.userBreakDetails?.message || null,
                userWorkingShiftFollowSchedule: !!result?.userWorkingShiftFollowSchedule
            }
            if (result?.userBreakDetails?.autoSplitMessage) {
                const alert = createAlert(result?.userBreakDetails?.autoSplitMessage, 'info');
                _states.alertMessages = [...this.state.alertMessages, alert]
            }
            this.setState({ ..._states });
            return user;
        }
        catch (error) {
            console.error(error);
        }
    }

    async loadData() {
        const { absenceStore, userStore, entity_id, router } = this.props;
        const { user_type, id: currentUserId } = userStore.currentUser;
        const redirectUserId = router?.location?.state?.redirectUserId;

        if (redirectUserId || (user_type === 'member' && !entity_id)) {
            const userIdToLoad = user_type === 'member' ? currentUserId : redirectUserId;
            await this.fetchAndSetUserData(userIdToLoad);
        }

        if (entity_id) {
            const _absence = await absenceStore.load(entity_id);
            const absence = _absence.absence;
            await this.fetchAndSetUserData(absence.user_id);
            const total_days = this.getBusinessDays(absence.from, absence.to);
            this.setState({
                total_hours: absence.hours,
                total_days,
                from: absence.from,
                to: absence.to,
                project_id: absence.project_id,
                start_time: absence.start_time,
                end_time: absence.end_time,
            });
        }
    }

    async handleUserChange(user_id) {
        if (user_id) {
            await this.fetchAndSetUserData(user_id);
        }
    }

    componentDidMount() {
        this.loadData();
    }

    updateField = async (field, value) => {
        const { t, commonStore } = this.props;
        try {
            if (field === 'from') {
                const { to } = this.state;
                await this.fetchAndSetUserData(this.state.loadedUser?.id, value, to);
                const effectiveTo = moment(value).isAfter(moment(to), 'day') ? value : to;
                const total_days = this.getBusinessDays(value, effectiveTo);
                this.setState({ from: value, to: effectiveTo, total_days });
                return;
            }
            if (field === 'to') {
                const { from } = this.state;
                await this.fetchAndSetUserData(this.state.loadedUser?.id, from, value);
                const total_days = this.getBusinessDays(from, value);
                this.setState({ to: value, total_days });
                return;
            }
            if (field === 'start_time' || field === 'end_time') {
                const mins = value ? getMinutesFromTimeInput(value) : null;
                this.setState({ [field]: mins }, this.handleTotalTimeChange);
                return;
            }
            this.setState({ [field]: value })
        } catch (error) {
            console.error(error);
            commonStore.addNotification(t(error.message || 'Something went wrong'), null, 'error');
            return;
        }
    }

    handleTotalTimeChange = () => {
        const { t, commonStore } = this.props;
        try {
            const { alertMessages, dailyContractedHours, start_time, end_time } = this.state;
            const total_hours = calculateHoursFromTimes(start_time, end_time);
            const _states = { total_hours }
            if (total_hours > 0 && dailyContractedHours < (total_hours * 60)) {
                const alert = createAlert(
                    t("Since you are taking comp-off for more than your normal work hours, please update the date accordingly"),
                    'error'
                )
                alertMessages.push(alert);
                _states.alertMessages = alertMessages
            }
            this.setState({ ..._states });
        }
        catch (error) {
            console.error(error);
            commonStore.addNotification(t(error.message || 'Something went wrong'), null, 'error');
            return;
        }
    }

    onSave(compOffSavedMinusUsed) {
        const { absenceStore, t, commonStore, handleCloseCompOffForm } = this.props;
        const { from, to, total_hours, start_time, end_time, loadedUser, project_id, negativeFlexitimeHoursLimit, total_days, alertMessages } = this.state;
        const totalHoursForCompOff = total_hours * total_days;
        const hasCompOffExceededLimit = parseInt(negativeFlexitimeHoursLimit) > ((compOffSavedMinusUsed / 60) - totalHoursForCompOff);
        if (hasCompOffExceededLimit) {
            const alert = createAlert(
                `${t("Total hours for comp-off has exceeded negative flexitime hours limit, please reduce hours or days accordingly")}. (${t("Limit")} : ${negativeFlexitimeHoursLimit} ${t("hours")})`,
                'error'
            )
            alertMessages.push(alert);
            this.setState({ alertMessages });
        }
        const { biztype } = commonStore.config;
        try {
            if (!total_hours || total_hours <= 0) {
                return commonStore.addNotification(t('Please enter valid from and to time'), null, 'error');
            }
            if (!project_id && biztype.data && biztype?.data?.allow_multiple_branches) {
                return commonStore.addNotification(t("Please select Project (Branch) and then try again"), null, 'warning');
            }
            if (moment(from).isBefore(moment().add(-1, 'year'), 'date')) {
                return commonStore.addNotification(t('Can not add or update sick leave older than 1 year'), null, 'error');
            }
            if (to < from) {
                return commonStore.addNotification(t('From date should be before to date'), null, 'error')
            }

            return absenceStore.saveCompOff({ project_id, from, to, total_hours: total_hours * 60, user_id: loadedUser.id, start_time, end_time }).then(result => {
                const cUser = this.props.userStore.currentUser;
                if (result.absence.user_id === cUser.id) {
                    this.props.userStore.pullUser().then(() => {
                        commonStore.addNotification(t('Saved'), null, 'success');
                        this.props.absenceStore.onFilter()
                        handleCloseCompOffForm();
                        return true;
                    });
                } else {
                    commonStore.addNotification(result.message, null, 'success');
                    this.props.absenceStore.onFilter()
                    handleCloseCompOffForm();
                    return true;
                }
            }).catch(e => {
                console.error(e);
            });
        }
        catch (error) {
            console.error(error)
        }
    }

    handleNavigateToTimebank = () => {
        this.props.router.navigate('/admin/timebank');
        this.props.handleCloseCompOffForm()
    }

    removeAlert = (messages) => {
        this.setState({ alertMessages: messages })
    }

    render() {
        const { absenceStore, commonStore, userStore, t, handleCloseCompOffForm } = this.props;
        const { loadedUser, total_hours, start_time, end_time, from, to, project_id, dailyContractedHours = 0,
            negativeFlexitimeHoursLimit = 0, total_days, alertMessages, userBreakMessage, userWorkingShiftFollowSchedule } = this.state;
        const { loading, currentEntity } = absenceStore;
        const { currentUser, loadingUser } = userStore;
        if (loading || loadingUser) return <LoadingSpinner />;
        const entity = currentEntity.absence || {};
        const { total_comp_off_hours_saved = 0, total_comp_off_hours_used = 0 } = loadedUser?.timeBankHours || {};
        const compOffSavedMinusUsed = total_comp_off_hours_saved - total_comp_off_hours_used;
        const dateTimeRules = commonStore.getDateTimeRules();
        return (
            <div className="primary-page">
                <AlertMessage messages={alertMessages} onClose={this.removeAlert} maxLimit={2} />
                <Row>
                    <Col>
                        <div className='mt-3'>
                            <div>{t('Total Comp leave count')}: <b>{loadedUser?.totalTimeBankAbsenceLog || 0}</b></div>
                            <div>{t('Total Comp hours used')}: <b>{totalTimeFormat(total_comp_off_hours_used, dateTimeRules)} {t('hr')}</b></div>
                            <u className='color-2550AC fw-600 cursor-pointer' onClick={() => this.handleNavigateToTimebank()}>{t('Go To Time Bank')}</u>
                        </div>
                    </Col>
                    <Col className='d-flex justify-content-end fs-5 gap-2'>
                        {currentUser.user_type !== 'member' &&
                            <RectangularSmallCard
                                customClassname="rectangle-card-timebank"
                                title={t('Max. negative limit')}
                                titleValue={`${totalTimeFormat(parseInt(Number(negativeFlexitimeHoursLimit * 60) || 0), dateTimeRules)} ${t('hr')}`}
                                pathColor='red'
                                rotationStart='5'
                                strokeWidth='10'
                            />
                        }
                        <RectangularSmallCard
                            customClassname="rectangle-card-timebank"
                            title={t('Comp Off')}
                            titleValue={`${totalTimeFormat(parseInt(compOffSavedMinusUsed || 0), dateTimeRules)} ${t('hr')}`}
                            pathColor='red'
                            rotationStart='5'
                            strokeWidth='10'
                        />
                    </Col>
                </Row>
                <Row className="mb-1">
                    {currentUser.user_type !== 'member' &&
                        <Col md={{ span: 6, order: 1 }}>
                            <label className='fw-600'>{t('Select Member')}</label>
                            <MembersWidget
                                key={'MemberWidget'}
                                className="form-control"
                                onChange={(value, full) => this.handleUserChange(value, full)}
                                value={loadedUser?.id}
                                defaultIfOne
                                placeholder={t('Select Member')}
                                reactSelectClassname={"custom-select-max-height-200"}
                                options={{ project: { project_id: entity.project_id } }}
                            />
                        </Col>
                    }
                    <Col md={{ order: 2 }} >
                        <label className='fw-600'>{t('Select') + " " + t('Project')}</label>
                        <ProjectsWidget
                            value={project_id || null}
                            onChange={e => this.updateField('project_id', e)}
                            userId={loadedUser?.id}
                        />
                    </Col>
                </Row>
                <Row className="mb-1">
                    <Col>
                        <label className='fw-600'>{t('From Date')}</label>
                        <input
                            type="date"
                            className="form-control"
                            value={from || ''}
                            onChange={e => this.updateField('from', e.target.value)}
                        />
                    </Col>
                    <Col>
                        <label className='fw-600'>{t('To Date')}</label>
                        <input
                            type="date"
                            className="form-control"
                            value={to || ''}
                            onChange={e => this.updateField('to', e.target.value)}
                        />
                    </Col>
                </Row>
                <Row className="mt-3">
                    <Col md={6}>
                        <label className='fw-600'>{t('Comp Leave Hours')}</label>
                        <div className={'d-flex justify-content-between mt-1'}>
                            <div>
                                <label className="small fw-600">{t('From Time')}</label>
                                <TimePickerInput
                                    value={start_time != null ? minutesToTimeInput(start_time, false) : ''}
                                    format={dateTimeRules?.time_format === 'hh:mm' ? 'HH:mm' : (dateTimeRules?.time_format || 'HH:mm')}
                                    onChange={value => this.updateField('start_time', value)}
                                />
                            </div>
                            <div>
                                <label className="small fw-600">{t('To Time')}</label>
                                <TimePickerInput
                                    value={end_time != null ? minutesToTimeInput(end_time, false) : ''}
                                    format={dateTimeRules?.time_format === 'hh:mm' ? 'HH:mm' : (dateTimeRules?.time_format || 'HH:mm')}
                                    onChange={value => this.updateField('end_time', value)}
                                />
                            </div>
                        </div>
                    </Col>
                    <Col md={6} className={'d-flex flex-column'}>
                        <span>{t('Working Shift')}: <b>{totalTimeFormat(dailyContractedHours || 0, dateTimeRules)}{t('hr')}</b>
                            <OverlayTrigger
                                key="compoff_working_shift"
                                placement="top"
                                overlay={
                                    <Tooltip id="tooltip-top">
                                        {userWorkingShiftFollowSchedule ? t('Working shift follows schedule and user profile') : t('Working shift follows only user profile')}
                                    </Tooltip>
                                }
                            >
                                <i className="fa fa-question-circle custom-theme-blue-color ms-1 cursor-pointer" />
                            </OverlayTrigger>
                            <span className='font-size-12 fw-bold ms-2'>
                                {userBreakMessage ? `(${userBreakMessage})` : ""}
                            </span>
                        </span>
                        <div className={'d-flex justify-content-between gap-2 align-items-end mt-2'}>
                            <Input
                                label={t('Hours')}
                                value={total_hours || '0'}
                                disabled
                            />
                            <Input
                                label={t('Total Hours')}
                                value={!isNaN(total_hours) ? parseInt(total_hours) * parseInt(total_days) : 0}
                                disabled
                            />
                        </div>
                    </Col>
                </Row>
                <div className='d-flex justify-content-end mt-2'>
                    <OverlayTrigger
                        key="compoff_update"
                        placement="top"
                        overlay={
                            this.props.entity_id ?
                                (<Tooltip id="tooltip-top">
                                    {t('You can not update the comp leave, Please delete and register new')}
                                </Tooltip>)
                                :
                                (<></>)
                        }
                    >
                        <Button variant="secondary" onClick={() => this.onSave(compOffSavedMinusUsed)} className="btn-wd btn-fill" disabled={this.props.entity_id}>
                            {t('Save')}
                        </Button>
                    </OverlayTrigger>
                    <Button variant="secondary" onClick={() => handleCloseCompOffForm()} className="btn-wd ml-10">
                        {t('Close')}
                    </Button>
                </div>
            </div>
        );
    }
}

export default inject('absenceStore', 'commonStore', 'userStore')(applicationRouter(withLocalization(observer(CompOffForm))));
